import { ApiError } from './http';

/**
 * What an API failure means to the person reading it.
 *
 * Most codes were rendered as the API's own sentence, which is written for the
 * engineer holding the request id. The one that matters most reads
 * "This Meridian identity is not authorized for the requested operation" - true,
 * and useless to an operator who has just pressed a button and cannot tell
 * whether they did something wrong, the product is broken, or their account is
 * simply not allowed to do this.
 *
 * These say which of those it is, and what to do next. The API's own words are
 * kept where they carry detail a person can act on, and replaced only where
 * they carry none.
 */

export interface Explained {
  /** One sentence, in the operator's terms. */
  what: string;
  /** What to do, when there is something. */
  next?: string;
  /** Whether pressing the same button again could plausibly work. */
  retryable: boolean;
}

export function explain(error: unknown, action = 'that'): Explained {
  if (!(error instanceof ApiError)) {
    // Every non-ApiError became "The API did not respond", which is a claim
    // about the backend made from a failure inside this app: a render-time
    // TypeError, an aborted parse, a shape the client could not walk. It sent
    // an operator to check a service that had answered correctly.
    return {
      what: 'Something in the app failed while handling the answer.',
      next: 'Reloading usually clears it. The backend may be fine.',
      retryable: true,
    };
  }

  /**
   * A tenant boundary is not a missing feature.
   *
   * It arrives as CAPABILITY_UNAVAILABLE and reads "this part of the product
   * is not built yet", which is wrong in both halves: the capability exists,
   * and this account is simply not scoped to this company. One sends a person
   * to ask for a feature to be built, the other to ask for access.
   */
  if (error.tenantGated) {
    return {
      what: 'This company is outside what your account is scoped to.',
      next: 'Ask an administrator to grant your account access to it.',
      retryable: false,
    };
  }

  switch (error.code) {
    case 'FORBIDDEN':
      return {
        // Authorization is two-sided: a global role and a role on the company
        // itself. A signed-in operator reading this page has already proven the
        // company access exists, so the accurate statement is that the role on
        // this membership permits reading and not changing - not that access is
        // missing, which would send someone asking for the wrong thing.
        what: `Your account can read this company but not ${action}.`,
        next: 'Your access to the company is fine; the role on it allows reading only. Nothing you did caused this and retrying will not help. Ask an administrator to raise your role on this company to operator.',
        retryable: false,
      };
    case 'UNAUTHORIZED':
      return {
        what: 'Your session has ended.',
        next: 'Sign in again; you will come back to this page.',
        retryable: false,
      };
    case 'CONFLICT':
      return {
        what: error.reason ?? error.message,
        next: 'Something changed since this screen loaded. Reload and read the current state before repeating this.',
        retryable: false,
      };
    case 'DEPENDENCY_UNAVAILABLE':
      return {
        what: 'The backend can serve this, but something it depends on is not available right now.',
        next: 'This is not a fault in your data. It usually clears on its own.',
        retryable: true,
      };
    case 'CAPABILITY_UNAVAILABLE':
      return {
        what: error.reason ?? 'This part of the product is not built yet.',
        next: 'Nothing here will make it work; it needs a backend capability that does not exist.',
        retryable: false,
      };
    case 'NETWORK':
      return {
        what: 'The API could not be reached.',
        next: 'Check the connection, or your session may have ended.',
        retryable: true,
      };
    case 'INVALID_REQUEST':
      return { what: error.reason ?? error.message, retryable: false };
    case 'NOT_FOUND':
      return {
        /**
         * The only branch that dropped the action.
         *
         * "That no longer exists, or is not visible to your account" under a
         * search box reads as though the scope had been deleted, when what
         * answered 404 was the search route. Naming the attempt is the
         * difference between a fact about the product and a fact about the
         * operator's data.
         */
        what: `Could not ${action}: what it asked for no longer exists, or is not visible to your account.`,
        retryable: false,
      };
    case 'REQUEST_TIMEOUT':
      return {
        what: `The backend took too long to ${action}.`,
        next: 'Nothing was changed by the attempt. Try again.',
        retryable: true,
      };
    case 'EVIDENCE_INSUFFICIENT':
      return {
        // The one refusal that is a finding rather than a fault: the request
        // was understood and the evidence did not reach the bar.
        what: error.reason ?? `There is not enough evidence to ${action}.`,
        next: 'More evidence comes from another walk over this bucket.',
        retryable: false,
      };
    case 'INTERNAL':
      return {
        what: 'The backend failed while handling this.',
        next: 'Nothing here can clear it. If it repeats, it needs an engineer and the request id.',
        retryable: false,
      };
    case 'CONTRACT_VIOLATION':
      return {
        // The API answered and the answer did not match the pinned contract.
        // Its own sentence names a schema key, which is written for whoever
        // regenerates the client.
        what: 'The backend answered in a shape this build does not recognise.',
        next: 'This build and the API have drifted apart. Nothing on this page can reconcile them.',
        retryable: false,
      };
    default:
      return {
        what: error.reason ?? error.message,
        retryable: error.retryable,
      };
  }
}

/**
 * The whole explanation as one line, for a caption - with the request id.
 *
 * This told operators that a failure "needs an engineer and the request id"
 * and then did not print the request id, though every ApiError carries one.
 * The identifier that makes a failure diagnosable was held in memory and
 * dropped at the exact moment the text said it would be needed, so what
 * reached the engineer was a screenshot of a sentence.
 *
 * It is appended rather than woven in, so it never displaces the explanation,
 * and only when the backend actually issued one.
 */
export function explainLine(error: unknown, action = 'that'): string {
  const e = explain(error, action);
  const line = e.next ? `${e.what} ${e.next}` : e.what;
  const id = error instanceof ApiError ? error.requestId : null;
  return id ? `${line} (${id})` : line;
}
